/**
 * Design system color themes.
 */

import type { ArrowLinkVariant } from 'components/ArrowLink';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';

export type ThemeName =
	| 'base'
	| 'black-friday'
	| 'black-friday-ribbon'
	| 'black-week'
	| 'black-week-ribbon'
	| 'campaign'
	| 'campaign-highlight'
	| 'campaign-summer'
	| 'crazy-deals'
	| 'jula-club'
	| 'jula-pro'
	| 'news'
	| 'sale'
	| 'sale-ribbon';

export function themeHasRibbon(theme: ThemeName | undefined) {
	return is.oneOf(
		theme,
		'sale-ribbon',
		'black-friday-ribbon',
		'black-week-ribbon',
	);
}

export function getThemeProps(theme: ThemeName | undefined = 'base') {
	const hasRibbon = themeHasRibbon(theme);
	const arrowLinkVariant: ArrowLinkVariant =
		theme === 'base'
			? 'branded'
			: is.oneOf(theme, 'news', 'sale', 'sale-ribbon', 'campaign-summer')
				? 'positive'
				: 'negative';

	return {
		arrowLinkVariant,
		hasRibbon,
		// TODO: Name? Maybe hasBackground, hasColor or hasTheme? Will probably
		// mostly be used to set padding but theme shouldn't know that.
		isPadded: theme !== 'base',
		baseClasses: cn(
			theme === 'black-friday' && 'bg-blackFriday text-blackFriday',
			theme === 'black-friday-ribbon' && 'bg-blackFriday text-blackFriday',
			theme === 'black-week' && 'bg-blackWeek text-blackWeek',
			theme === 'black-week-ribbon' && 'bg-blackWeek text-blackWeek',
			theme === 'campaign' && 'bg-campaign text-campaign',
			theme === 'campaign-highlight' && 'bg-campaign text-campaign',
			theme === 'crazy-deals' && [
				'bg-crazyDeals text-crazyDeals',
				'bg-radial-[145%_145%_at_100%_0%] from-crazyDealsStop1 from-0% via-crazyDealsStop2 via-20% to-crazyDeals to-50%',
			],
			theme === 'sale' && 'bg-sale text-sale',
			theme === 'sale-ribbon' && 'bg-sale text-sale',
			theme === 'campaign-summer' && 'bg-campaignSummer text-campaignSummer',
			theme === 'jula-club' && 'bg-julaclub text-julaClub',
			theme === 'jula-pro' && 'bg-julapro text-julaPro',
			theme === 'news' && 'bg-news text-news',
		),
		highlightTextClass: cn(
			theme === 'campaign-highlight' && 'text-campaignHighlight',
			theme === 'sale' && 'text-saleHighlight',
			theme === 'sale-ribbon' && 'text-saleHighlight',
			theme === 'campaign-summer' && 'text-campaignSummerHighlight',
			theme === 'black-friday' && 'text-blackFridayHighlight',
			theme === 'black-friday-ribbon' && 'text-blackFridayHighlight',
			theme === 'black-week' && 'text-blackWeekHighlight',
			theme === 'black-week-ribbon' && 'text-blackWeekHighlight',
		),
		ribbonBgClass: cn(
			hasRibbon &&
				'ribbon-stripes forced-color-adjust-none forced-colors:ribbon-stripe-a-[CanvasText] forced-colors:ribbon-stripe-b-[Canvas]',
			theme === 'sale-ribbon' &&
				'ribbon-stripe-a-saleHighlight ribbon-stripe-b-sale',
			theme === 'black-friday-ribbon' &&
				'ribbon-stripe-a-blackFridayHighlight ribbon-stripe-b-blackFriday',
			theme === 'black-week-ribbon' &&
				'ribbon-stripe-a-blackWeekHighlight ribbon-stripe-b-blackWeek',
		),
	};
}
